import { Alert } from '@warp-ds/react/components/alert';
import { Button } from '@warp-ds/react/components/button';
import { Modal } from '@warp-ds/react/components/modal';
import { TextField } from '@warp-ds/react/components/textfield';
import { useRef, useState } from 'react';

import { useConfig } from 'shared/config/ConfigContext';
import { formatClientDate } from 'shared/util/datetime-fns';
import { useLmsDestination } from '@/components/lms/hooks/useLmsDestination';
import type { LmsFilters } from '@/interfaces/lms';

interface LmsSaveSearchProps {
    filters: LmsFilters;
    small?: boolean;
    isLoggedIn?: boolean;
}

export const LmsSaveSearch = ({ filters, small, isLoggedIn }: LmsSaveSearchProps) => {
    const { apiUrlLMS } = useConfig();
    const destination = useLmsDestination();

    const searchNameRef = useRef<HTMLInputElement>(null);
    const [showModal, setShowModal] = useState(false);
    const [success, setSuccess] = useState<boolean | null>(null);

    const isFiltersSelected = Object.values(filters).some((value) => {
        if (value === undefined || value === null) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object') return Object.keys(value).length > 0;
        return true;
    });

    const defaultSearchName = () => {
        if (destination) return `Restplasser til ${destination}`;
        if (filters.avreise) return `Restplasser ${formatClientDate(filters.avreise.from)} - ${formatClientDate(filters.avreise.to)}`;
        if (filters.fra?.length > 0) return `Restplasser fra ${filters.fra.join('+')}`;
        if (filters.med?.length > 0) return `Restplasser med ${filters.med.join('+')}`;
        return 'Restplasser - nytt søk';
    };

    const toggleModal = () => {
        setSuccess(null);
        setShowModal(!showModal);
    };

    const handleSaveSearch = () => {
        setSuccess(null);
        fetch(`${apiUrlLMS}/save-search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                searchName: searchNameRef.current?.value,
                url: new URLSearchParams(window?.location.search).toString() || undefined,
            }),
            credentials: 'include',
        })
            .then((res) => {
                setSuccess(res.ok);
            })
            .catch(() => {
                setSuccess(false);
            });
    };

    const renderModalButtons = () =>
        isLoggedIn ? (
            <>
                <Button secondary quiet className="mr-16" onClick={toggleModal}>
                    {success ? 'Lukk' : 'Avbryt'}
                </Button>
                <Button primary disabled={!isFiltersSelected || success} className="truncate" onClick={handleSaveSearch}>
                    Lagre
                </Button>
            </>
        ) : (
            <>
                <Button href="/auth/login" className="mr-16">
                    Ny bruker
                </Button>
                <Button primary href="/auth/login">
                    Logg inn
                </Button>
            </>
        );

    return (
        <div className={small ? 'md:hidden' : 'hidden md:block px-16 pb-16'}>
            <Button small={small} utility fullWidth={!small} onClick={toggleModal}>
                Lagre søk
            </Button>
            <Modal title="Lagre søk" open={showModal} right footer={renderModalButtons()} onDismiss={toggleModal}>
                {isLoggedIn ? (
                    <>
                        <TextField label="Navn" defaultValue={defaultSearchName()} required autoFocus ref={searchNameRef} />
                        {!isFiltersSelected && (
                            <p className="text-s s-text-negative mt-8">Vennligst velg minst ett filter for å lagre et søk.</p>
                        )}
                        <Alert role="alert" type={success ? 'positive' : 'negative'} show={success !== null} className="mt-16">
                            {success ? 'Søket er lagret!' : 'Ooops... En feil oppsto under lagring av søket, vennligst prøv igjen senere.'}
                        </Alert>
                    </>
                ) : (
                    <div className="pb-8">Du må logge inn for å kunne lagre søket.</div>
                )}
            </Modal>
        </div>
    );
};
